/**
 * API client. Every response must carry `X-KGR: 1` and the expected content type — anything
 * else (e.g. Pages "fail open" serving index.html) is treated as a network failure, never as data.
 */
import type { ApiErrorBody, ErrorCode } from '../../shared/errors';

export class ApiFailure extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode,
    message?: string,
    public readonly details?: unknown,
  ) {
    super(message ?? code);
  }
}

/** Could not reach the API (offline, timeout, or a non-API response). */
export class NetworkFailure extends Error {
  constructor(message = 'network') {
    super(message);
  }
}

export const isNetworkFailure = (e: unknown): e is NetworkFailure => e instanceof NetworkFailure;
export const isUnauthorized = (e: unknown) => e instanceof ApiFailure && e.status === 401;

let serverOffsetMs = 0;
/** Estimated server time (from response Date headers), used for offline check-ins. */
export function serverNow(): number {
  return Date.now() + serverOffsetMs;
}

const listeners = new Set<() => void>();
/** Called on any 401 so the auth layer can route to login. */
export function onUnauthorized(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
  body?: unknown;
  /** Raw body (uploads). Sent with the given content type. */
  raw?: Blob | ArrayBuffer;
  rawType?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
}

function combineSignals(a: AbortSignal | undefined, timeoutMs: number): AbortSignal {
  const t = AbortSignal.timeout(timeoutMs);
  if (!a) return t;
  if ('any' in AbortSignal) return AbortSignal.any([a, t]);
  return a;
}

export async function apiRaw(path: string, opts: ApiOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.raw !== undefined) {
    body = opts.raw;
    headers['content-type'] = opts.rawType ?? 'application/octet-stream';
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers['content-type'] = 'application/json';
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      credentials: 'same-origin',
      signal: combineSignals(opts.signal, opts.timeoutMs ?? 15_000),
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError' && opts.signal?.aborted) throw e;
    throw new NetworkFailure((e as Error)?.message);
  }
  if (res.headers.get('x-kgr') !== '1') throw new NetworkFailure('bad_contract');
  const date = res.headers.get('date');
  if (date) {
    const d = Date.parse(date);
    if (!Number.isNaN(d)) serverOffsetMs = d - Date.now();
  }
  if (!res.ok && res.status !== 304 && res.status !== 206) {
    let code: ErrorCode = 'internal';
    let message: string | undefined;
    let details: unknown;
    try {
      const j = (await res.json()) as ApiErrorBody;
      code = j.error.code;
      message = j.error.message;
      details = j.error.details;
    } catch {
      /* non-JSON error */
    }
    if (res.status === 401) for (const fn of listeners) fn();
    throw new ApiFailure(res.status, code, message, details);
  }
  return res;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await apiRaw(path, opts);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) throw new NetworkFailure('bad_contract');
  return (await res.json()) as T;
}
